'use client'

// The mushrooms — his wish, grown where he asked. mushrooms.glb bakes the
// oversized caps at their world positions (the eatMushroom_N POIs in
// sceneConfig sit at their feet).
//
// The export carries NO material table (four draco meshes, no material
// index), so the loader hands out its plain default material and the
// dither sweep skips it: rendererDither only patches node materials, and
// that is why the caps rendered smooth against a dithered town. We build
// our own node material instead of converting a table that is not there.
//
// The cap atlas is a loose KTX2 rather than baked into the GLB, same
// reasoning as the billboards: Sam can repaint the caps without
// re-exporting a model, and ETC1S stays compressed in VRAM. 1024 square,
// 155KB against the 2048 JPEG master's 1.3MB.
//
// The .jpg beside it is the master. To re-encode after repainting:
//
//   sharp(src).resize(1024, 1024, { fit: 'fill' }).png() -> in.png
//   toktx --t2 --encode etc1s --genmipmap --assign_oetf srgb out.ktx2 in.png
//
// Same settings lib/glb/optimize.mjs uses for model textures.

import { useEffect, useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useThree } from '@react-three/fiber'
import { useModel, getKTX2 } from '../hooks/useModel'

const ATLAS = '/textures/Mushroom_Psilocybe_Emissive_AbstractB.ktx2'

// Decoded once for the life of the page, not once per effect run. Four
// mushroom meshes behind a dev double-invoke asked the transcoder for the
// same 1024 square four times over. Shared, so component cleanup must
// leave it alone: whoever unmounts last does not get to dispose it.
const _atlas = new Map()
function atlasTexture(ktx2, url) {
  if (!_atlas.has(url)) {
    _atlas.set(url, ktx2.loadAsync(url).then((tex) => {
      // No flipY: a compressed texture cannot be flipped on the GPU, so
      // orientation is settled at encode time. The file carries
      // KTXorientation rd (top-down), which is what glTF UVs expect.
      tex.colorSpace = THREE.SRGBColorSpace
      tex.anisotropy = 4
      tex.userData.shared = true
      return tex
    }))
  }
  return _atlas.get(url)
}

export function Mushrooms(props) {
  const { scene } = useModel('/models/mushrooms.glb')
  const gl = useThree((s) => s.gl)

  // Built once and kept: the texture arrives after the geometry and is
  // assigned into these materials below, so a late atlas does not rebuild
  // the whole scene graph.
  const { object, materials } = useMemo(() => {
    const s = scene.clone(true)
    const converted = new Map()
    s.traverse((o) => {
      if (!o.isMesh) return
      const src = o.material
      let mat = converted.get(src)
      if (!mat) {
        mat = new THREE.MeshStandardNodeMaterial()
        if (src?.color) mat.color = src.color.clone()
        // Sam's browns, underneath whatever the atlas paints on top.
        mat.vertexColors = !!o.geometry?.attributes?.color
        mat.roughness = 0.75
        mat.metalness = 0
        converted.set(src, mat)
      }
      o.material = mat
      o.castShadow = true
      o.receiveShadow = true
    })
    return { object: s, materials: [...converted.values()] }
  }, [scene])

  // Load and assign in one effect so the texture's life is tied to
  // exactly the materials using it: split across two, a materials change
  // disposed the atlas out from under the set that had just asked for it.
  useEffect(() => {
    let dead = false
    atlasTexture(getKTX2(gl), ATLAS).then((tex) => {
      if (dead) return
      // Map only, no emissive: the caps at full emissive swamped the
      // lighting and the whole patch read as unlit. They live in the
      // world's light like everything else now.
      for (const mat of materials) {
        mat.map = tex
        mat.needsUpdate = true
      }
    }).catch((err) => {
      console.warn('[Mushrooms] atlas failed to load:', err?.message || err)
    })
    // The atlas is shared and outlives this mount, so nothing is disposed
    // here; the materials built above are this component's to drop.
    return () => { dead = true }
  }, [gl, materials])

  return <primitive object={object} {...props} dispose={null} />
}
